import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | undefined} */
  #openTriggerAbort;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.#attachOpenTrigger();
    queueMicrotask(() => this.#attachOpenTrigger());
  }

  /**
   * After Section Rendering / morph, the trigger node can be replaced; `connectedCallback`
   * does not run again, so we re-bind from `Component.updatedCallback()`.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#attachOpenTrigger();
  }

  #attachOpenTrigger() {
    this.#openTriggerAbort?.abort();
    this.#openTriggerAbort = new AbortController();
    const { signal } = this.#openTriggerAbort;
    /** @type {Element[]} */
    const triggers = Array.from(this.querySelectorAll('[data-testid="cart-drawer-trigger"]'));
    for (const trigger of triggers) {
      trigger.addEventListener('click', this.#onCartTriggerClick, { signal, capture: true });
    }
  }

  disconnectedCallback() {
    this.#openTriggerAbort?.abort();
    this.#openTriggerAbort = undefined;
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #onCartTriggerClick = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    this.open();
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    if (!(this.refs?.dialog instanceof HTMLDialogElement)) {
      this.updatedCallback();
    }
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
